import { useState, useEffect, useCallback } from 'react';
import { B } from '../constants/brand';
import { api } from '../api/client';
import { Btn, Card, Spinner, ErrorMsg, Badge, Input, InfoNote, EmptyState,
         SectionHeader, useToast, Icon } from '../components/ui';
import { InlineSearchPicker, useDealPickerItems } from '../components/tasks/TaskComponents';
import { fmt } from '../utils/format';

// ============================================================================
// ShareFileSettings — Settings → "ShareFile" tab (Admin).
//
// Connects Harvus to a ShareFile account and files what outside parties upload
// there into the deals they belong to.
//
// Three panels, in the order an admin actually works through them:
//   1. Connection — subdomain + OAuth client, then "Connect" (a ShareFile
//      consent redirect). Shows the live token state, including the one failure
//      that matters: a refresh token that has aged out of the account's
//      "Re-authenticate after" policy, which needs a human to reconnect.
//   2. Watched folders — browse ShareFile, then watch a folder either MAPPED to
//      a deal (auto-import) or UNMAPPED (its files land in the inbox). That
//      single choice is the whole routing model.
//   3. Inbox — files from unmapped folders, with suggested deals, assigned one
//      click at a time.
//
// Nothing in ShareFile is ever moved or deleted from here — see the module
// header in api/src/functions/sharefile.js.
// ============================================================================

const REL = (d) => (d ? fmt.date(d) : '—');

// ── Connection ──────────────────────────────────────────────────────────────
const ConnectionPanel = ({ status, onChanged }) => {
  const { toast } = useToast();
  const [subdomain, setSubdomain]       = useState(status.subdomain || '');
  const [clientId, setClientId]         = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [saving, setSaving]             = useState(false);
  const [busy, setBusy]                 = useState(false);
  const [waiting, setWaiting]           = useState(false);

  useEffect(() => { setSubdomain(status.subdomain || ''); }, [status.subdomain]);

  // ShareFile consent happens in a SEPARATE TAB (it refuses to render in a
  // frame), so this page has no way of knowing the connection succeeded — it
  // would sit on "Not connected" until someone reloaded it by hand. So after
  // opening the consent tab we re-read the status on a timer, and stop as soon
  // as it flips to connected (or after a couple of minutes, if the admin
  // abandoned the consent screen).
  useEffect(() => {
    if (!waiting) return undefined;
    if (status.connected) { setWaiting(false); return undefined; }
    let ticks = 0;
    const timer = setInterval(() => {
      if (++ticks > 40) { setWaiting(false); return; }   // ~2 minutes
      onChanged();
    }, 3000);
    return () => clearInterval(timer);
  }, [waiting, status.connected, onChanged]);

  const save = async () => {
    if (!subdomain.trim()) { toast('Enter your ShareFile subdomain first.', 'error'); return; }
    setSaving(true);
    try {
      await api.put('sharefile/config', {
        subdomain: subdomain.trim(),
        clientId: clientId.trim() || undefined,
        clientSecret: clientSecret || undefined,
      });
      setClientSecret('');       // write-only: never round-tripped back
      toast('ShareFile settings saved.');
      onChanged();
    } catch (e) { toast(e.message || 'Could not save.', 'error'); }
    finally { setSaving(false); }
  };

  const connect = async () => {
    setBusy(true);
    try {
      const { url } = await api.get('sharefile/auth-url');
      // Full navigation in a new tab: ShareFile refuses to render consent in a frame.
      window.open(url, '_blank', 'noopener');
      setWaiting(true);
      toast('Approve Harvus in the ShareFile tab — this page updates by itself.');
    } catch (e) { toast(e.message || 'Could not start the connection.', 'error'); }
    finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect ShareFile? Watched folders and import history are kept, '
                      + 'so reconnecting resumes rather than re-importing everything.')) return;
    setBusy(true);
    try { await api.post('sharefile/disconnect', {}); toast('Disconnected.'); onChanged(); }
    catch (e) { toast(e.message || 'Could not disconnect.', 'error'); }
    finally { setBusy(false); }
  };

  const state = status.needsReconnect ? 'reconnect' : status.connected ? 'connected' : 'setup';

  return (
    <Card style={{ marginBottom: 16 }}>
      <SectionHeader title="ShareFile connection" action={
        state === 'connected' ? <Badge color={B.tealDk}>Connected</Badge>
        : state === 'reconnect' ? <Badge color={B.red || '#b91c1c'}>Reconnect needed</Badge>
        : <Badge color={B.textMd}>Not connected</Badge>
      } />

      {state === 'reconnect' && (
        <div style={{ background: '#fef2f2', border: `1px solid #fecaca`, borderRadius: 8,
                      padding: 12, marginBottom: 12, fontSize: 13, color: '#991b1b' }}>
          <strong>ShareFile needs to be reconnected.</strong> A ShareFile refresh token only lives as
          long as your account&apos;s <em>Re-authenticate after</em> security policy (Account Settings →
          Security → Security Policy), so this is expected periodically rather than a fault. Press
          Connect below to re-approve. Importing is paused until then; nothing is lost — the next scan
          picks up everything that arrived in the meantime.
          {status.lastError && (
            <div style={{ marginTop: 6, fontFamily: 'monospace', fontSize: 11, opacity: 0.8 }}>
              {status.lastError}
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 12 }}>
        <Input label="ShareFile subdomain" value={subdomain} onChange={setSubdomain}
               placeholder="harvus (from harvus.sharefile.com)" required />
        <Input label={`OAuth client id${status.hasClientId ? ' (set)' : ''}`}
               value={clientId} onChange={setClientId}
               placeholder={status.hasClientId ? 'Stored — type to replace' : 'From your ShareFile API key'} />
        <Input label={`OAuth client secret${status.hasClientSecret ? ' (set)' : ''}`} type="password"
               value={clientSecret} onChange={setClientSecret}
               placeholder={status.hasClientSecret ? 'Stored — type to replace' : 'Write-only'} />
      </div>

      <InfoNote>
        Get the client id and secret by requesting an API key at{' '}
        <code>https://api.sharefile.com/</code> (ShareFile → Developer / API keys). Register this exact
        redirect URI on the key:{' '}
        {status.redirectUri
          ? <code style={{ wordBreak: 'break-all' }}>{status.redirectUri}</code>
          : <strong>not configured — set SHAREFILE_PUBLIC_BASE_URL (or SHAREFILE_REDIRECT_URI) on the
              Function App</strong>}
        . The secret is stored encrypted and is never sent back to this page.
      </InfoNote>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
        <Btn onClick={save} loading={saving} variant="secondary">Save settings</Btn>
        <Btn onClick={connect} loading={busy}
             disabled={!status.hasClientSecret || !status.redirectUriConfigured}>
          {state === 'setup' ? 'Connect ShareFile' : 'Reconnect'}
        </Btn>
        <Btn onClick={onChanged} variant="ghost">Refresh</Btn>
        {(status.connected || status.needsReconnect) && (
          <Btn onClick={disconnect} loading={busy} variant="ghost">Disconnect</Btn>
        )}
      </div>

      {waiting && !status.connected && (
        <div style={{ marginTop: 10, fontSize: 13, color: B.textMd }}>
          Waiting for the ShareFile approval to come back…
        </div>
      )}

      <div style={{ marginTop: 12, fontSize: 12, color: B.textMd, display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 8 }}>
        <div>Connected by: <strong>{status.connectedBy || '—'}</strong></div>
        <div>Last scan: <strong>{REL(status.lastSyncUTC)}</strong></div>
        <div>Files imported: <strong>{status.counts?.imported ?? 0}</strong></div>
        <div>Awaiting assignment: <strong>{status.counts?.inboxPending ?? 0}</strong></div>
      </div>

      {!status.blobConfigured && (
        <ErrorMsg msg="Blob Storage is not configured, so files cannot be imported yet." />
      )}
    </Card>
  );
};

// ── ShareFile folder browser ────────────────────────────────────────────────
const FolderBrowser = ({ onWatch, watchedIds }) => {
  const [stack, setStack] = useState([{ id: 'home', name: 'Home' }]);
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr]     = useState(null);
  const here = stack[stack.length - 1];

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr(null);
    api.get(`sharefile/browse?id=${encodeURIComponent(here.id)}`)
      .then(d => { if (!cancelled) { if (d.error) setErr(d.error); else setData(d); } })
      .catch(e => { if (!cancelled) setErr(e.message || 'Could not read ShareFile'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [here.id]);

  return (
    <Card style={{ marginBottom: 16 }}>
      <SectionHeader title="Browse ShareFile" />
      <div style={{ fontSize: 13, color: B.textMd, marginBottom: 8, display: 'flex',
                    gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
        {stack.map((s, i) => (
          <span key={`${s.id}-${i}`}>
            {i > 0 && <span style={{ margin: '0 4px' }}>/</span>}
            <button type="button" onClick={() => setStack(stack.slice(0, i + 1))}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                             color: i === stack.length - 1 ? B.text : B.tealDk,
                             fontWeight: i === stack.length - 1 ? 600 : 400 }}>
              {s.name}
            </button>
          </span>
        ))}
      </div>

      {loading && <Spinner label="Reading ShareFile…" />}
      {err && <ErrorMsg msg={err} />}
      {!loading && !err && data && (
        <div>
          {!data.folders.length && !data.files.length && (
            <div style={{ fontSize: 13, color: B.textLt, padding: '8px 0' }}>This folder is empty.</div>
          )}
          {data.folders.map(f => (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8,
                                     padding: '7px 0', borderBottom: `1px solid ${B.border}` }}>
              <Icon name="folder" size={15} />
              <button type="button" onClick={() => setStack([...stack, { id: f.id, name: f.name }])}
                      style={{ flex: 1, textAlign: 'left', background: 'none', border: 'none',
                               padding: 0, cursor: 'pointer', color: B.text, fontSize: 14 }}>
                {f.name}
              </button>
              {watchedIds.has(f.id)
                ? <Badge color={B.tealDk}>Watched</Badge>
                : <Btn size="sm" variant="secondary" onClick={() => onWatch(f)}>Watch this folder</Btn>}
            </div>
          ))}
          {data.files.slice(0, 25).map(f => (
            <div key={f.id} style={{ display: 'flex', gap: 8, padding: '5px 0', fontSize: 13,
                                     color: B.textMd }}>
              <Icon name="file" size={14} />
              <span style={{ flex: 1 }}>{f.name}</span>
              <span>{f.sizeBytes == null ? '' : fmt.number(Math.round(f.sizeBytes / 1024)) + ' KB'}</span>
            </div>
          ))}
          {data.files.length > 25 && (
            <div style={{ fontSize: 12, color: B.textLt, paddingTop: 4 }}>
              …and {data.files.length - 25} more file(s) in this folder.
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

// ── Watched folders ─────────────────────────────────────────────────────────
const WatchRow = ({ watch, dealPickerItems, onSave, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [pick, setPick] = useState(
    watch.dealDetailId ? `detail:${watch.dealDetailId}` : watch.dealId ? `deal:${watch.dealId}` : '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    const item = dealPickerItems.find(i => i.value === pick);
    await onSave(watch, {
      dealId: item?.dealID || null,
      dealDetailId: item?.dealDetailID || null,
    });
    setSaving(false); setEditing(false);
  };

  const last = watch.lastScan;
  return (
    <div style={{ padding: '10px 0', borderBottom: `1px solid ${B.border}` }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Icon name="folder" size={15} />
        <strong style={{ fontSize: 14 }}>{watch.folderName || watch.folderId}</strong>
        {watch.mode === 'import'
          ? <Badge color={B.tealDk}>→ {watch.dealName || 'deal'}{watch.detailDescription ? ` · ${watch.detailDescription}` : ''}</Badge>
          : <Badge color={B.gold || '#b45309'}>→ Inbox (assign each file)</Badge>}
        <span style={{ flex: 1 }} />
        <Btn size="sm" variant="ghost" onClick={() => setEditing(!editing)}>
          {editing ? 'Cancel' : 'Change deal'}
        </Btn>
        <Btn size="sm" variant="ghost" onClick={() => onDelete(watch)}>Stop watching</Btn>
      </div>

      <div style={{ fontSize: 12, color: B.textMd, marginTop: 3 }}>
        Last scan {REL(watch.lastScanUTC)}
        {last && ` · imported ${last.imported || 0}, to inbox ${last.inboxed || 0}, `
               + `already had ${last.duplicates || 0}${last.remaining ? `, ${last.remaining} queued` : ''}`}
        {last?.errors?.length ? ` · ${last.errors.length} problem(s): ${last.errors[0].error}` : ''}
      </div>

      {editing && (
        <div style={{ marginTop: 8, maxWidth: 480 }}>
          <InlineSearchPicker
            label="Import everything in this folder to"
            items={dealPickerItems} value={pick} onChange={setPick}
            placeholder="Search deals and capital types…"
            clearLabel="Nothing — send files to the inbox instead" />
          <Btn size="sm" onClick={save} loading={saving} style={{ marginTop: 8 }}>Save</Btn>
        </div>
      )}
    </div>
  );
};

// ── Inbox ───────────────────────────────────────────────────────────────────
const InboxRow = ({ item, dealPickerItems, onAssign, onDismiss }) => {
  const [pick, setPick] = useState('');
  const [busy, setBusy] = useState(false);

  const assign = async (value) => {
    const item2 = dealPickerItems.find(i => i.value === value);
    if (!item2) return;
    setBusy(true);
    await onAssign(item, { dealId: item2.dealID, dealDetailId: item2.dealDetailID || null });
    setBusy(false);
  };

  const preview = async () => {
    try {
      const { url } = await api.get(`sharefile/inbox/${item.inboxId}/download-url`);
      window.open(url, '_blank', 'noopener');
    } catch { /* the button just does nothing if ShareFile refuses */ }
  };

  return (
    <div style={{ padding: '10px 0', borderBottom: `1px solid ${B.border}` }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 14 }}>{item.fileName}</strong>
        <span style={{ fontSize: 12, color: B.textMd }}>
          {item.filePath || '—'}
          {item.sizeBytes != null && ` · ${fmt.number(Math.round(item.sizeBytes / 1024))} KB`}
          {item.uploadedByName && ` · from ${item.uploadedByName}`}
          {item.uploadedUTC && ` · ${REL(item.uploadedUTC)}`}
        </span>
        <span style={{ flex: 1 }} />
        <Btn size="sm" variant="ghost" onClick={preview}>Open in ShareFile</Btn>
        <Btn size="sm" variant="ghost" onClick={() => onDismiss(item)}>Dismiss</Btn>
      </div>

      {item.status === 'error' && <ErrorMsg msg={item.lastError} />}

      {/* One-click chips for the suggested deals, then the full picker. */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 6 }}>
        {(item.suggestions || []).slice(0, 3).map((s, i) => {
          const value = s.dealDetailId ? `detail:${s.dealDetailId}` : `deal:${s.dealId}`;
          const known = dealPickerItems.find(p => p.value === value);
          if (!known) return null;
          return (
            <Btn key={`${value}-${i}`} size="sm" variant="secondary" loading={busy}
                 onClick={() => assign(value)}>
              {known.label}
            </Btn>
          );
        })}
        <div style={{ minWidth: 260, flex: 1 }}>
          <InlineSearchPicker
            items={dealPickerItems} value={pick}
            onChange={(v) => { setPick(v); if (v) assign(v); }}
            placeholder="Or search for a deal…" />
        </div>
      </div>
    </div>
  );
};

// ── Page ────────────────────────────────────────────────────────────────────
export const ShareFileSettings = () => {
  const { toast } = useToast();
  const { dealPickerItems } = useDealPickerItems();
  const [status, setStatus]   = useState(null);
  const [watches, setWatches] = useState([]);
  const [inbox, setInbox]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState(null);
  const [scanning, setScanning] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [hookResults, setHookResults] = useState(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const s = await api.get('sharefile/status');
      setStatus(s);
      if (s.available) {
        const [w, i] = await Promise.all([
          api.get('sharefile/watches').catch(() => ({ watches: [] })),
          api.get('sharefile/inbox').catch(() => ({ items: [] })),
        ]);
        setWatches(w.watches || []);
        setInbox(i.items || []);
      }
    } catch (e) { setErr(e.message || 'Could not load ShareFile settings'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const scan = async () => {
    setScanning(true);
    try {
      const r = await api.post('sharefile/scan', {}, { timeout: 120000 });
      const t = r.totals;
      // A scan that could not run at all reports why rather than "finished".
      if (!r.connected) toast('ShareFile is not connected.', 'error');
      else if (r.error) toast(r.error, 'error');
      else if (!t) toast(r.note || 'Nothing to scan yet.');
      else {
        toast(`Imported ${t.imported}, ${t.inboxed} to the inbox`
            + `${t.remaining ? `, ${t.remaining} still queued` : ''}`
            + `${t.errors ? `, ${t.errors} problem(s)` : ''}`);
      }
      await load();
    } catch (e) { toast(e.message || 'Scan failed.', 'error'); }
    finally { setScanning(false); }
  };

  // Webhooks only make pickup FASTER — the 15-minute sweep is what guarantees
  // it — so a folder that refuses a subscription is reported, not treated as a
  // failure. ShareFile's subscription contract varies by release.
  const subscribe = async () => {
    setSubscribing(true);
    try {
      const r = await api.post('sharefile/subscribe', {}, { timeout: 60000 });
      setHookResults(r.results || []);
      const ok = (r.results || []).filter(x => x.ok).length;
      toast(ok
        ? `Live notifications on for ${ok} of ${(r.results || []).length} folder(s).`
        : 'ShareFile would not register notifications — the 15-minute sweep still covers you.');
    } catch (e) { toast(e.message || 'Could not register notifications.', 'error'); }
    finally { setSubscribing(false); }
  };

  const watchFolder = async (folder) => {
    try {
      await api.put('sharefile/watches', {
        folderId: folder.id, folderName: folder.name, folderPath: folder.name });
      toast(`Watching "${folder.name}". Files land in the inbox until you map it to a deal.`);
      await load();
    } catch (e) { toast(e.message || 'Could not watch that folder.', 'error'); }
  };

  const saveWatch = async (watch, patch) => {
    try {
      await api.put('sharefile/watches', {
        folderId: watch.folderId, folderName: watch.folderName, folderPath: watch.folderPath,
        ...patch });
      toast('Saved.');
      await load();
    } catch (e) { toast(e.message || 'Could not save.', 'error'); }
  };

  const deleteWatch = async (watch) => {
    if (!window.confirm(`Stop watching "${watch.folderName}"? Nothing in ShareFile is deleted, `
                      + 'and files already imported stay on their deals.')) return;
    try { await api.del(`sharefile/watches/${watch.watchId}`); await load(); }
    catch (e) { toast(e.message || 'Could not stop watching.', 'error'); }
  };

  const assign = async (item, target) => {
    try {
      const r = await api.post(`sharefile/inbox/${item.inboxId}/assign`, target,
                               { timeout: 120000 });
      toast(r.folderPath ? `Imported into ${r.folderPath}.` : 'Imported.');
      await load();
    } catch (e) { toast(e.message || 'Could not import that file.', 'error'); }
  };

  const dismiss = async (item) => {
    try { await api.post(`sharefile/inbox/${item.inboxId}/dismiss`, {}); await load(); }
    catch (e) { toast(e.message || 'Could not dismiss.', 'error'); }
  };

  if (loading) return <Spinner label="Loading ShareFile settings…" />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;
  if (!status?.available) {
    return <EmptyState icon="info" title="ShareFile is not set up yet"
             message={status?.reason || 'Run database migration 158, then reload this page.'} />;
  }

  const watchedIds = new Set(watches.map(w => w.folderId));

  return (
    <div>
      <ConnectionPanel status={status} onChanged={load} />

      {status.connected && (
        <>
          <Card style={{ marginBottom: 16 }}>
            <SectionHeader title={`Watched folders (${watches.length})`} action={
              <span style={{ display: 'flex', gap: 6 }}>
                <Btn size="sm" variant="ghost" onClick={subscribe} loading={subscribing}
                     disabled={!watches.length}>Speed up with notifications</Btn>
                <Btn size="sm" onClick={scan} loading={scanning}
                     disabled={!watches.length}>Scan now</Btn>
              </span>} />
            <InfoNote>
              Harvus checks these folders every 15 minutes. A folder <strong>mapped to a deal</strong>
              {' '}imports straight onto that deal — OCR, document-type tagging, AI folder filing and
              the SharePoint copy all run exactly as if someone had uploaded the file in the app. An
              <strong> unmapped</strong> folder sends its files to the inbox below instead. Files are
              never moved or removed from ShareFile.
            </InfoNote>
            {hookResults && (
              <div style={{ fontSize: 12, color: B.textMd, marginBottom: 8 }}>
                {hookResults.map((r, i) => (
                  <div key={`${r.folder}-${i}`}>
                    {r.ok ? '✓' : '—'} {r.folder}
                    {r.ok ? ' — live notifications on' : ` — ${r.error} (the 15-minute sweep still covers it)`}
                  </div>
                ))}
              </div>
            )}
            {!watches.length
              ? <div style={{ fontSize: 13, color: B.textLt, padding: '8px 0' }}>
                  Nothing is being watched yet — pick a folder below.
                </div>
              : watches.map(w => (
                  <WatchRow key={w.watchId} watch={w} dealPickerItems={dealPickerItems}
                            onSave={saveWatch} onDelete={deleteWatch} />
                ))}
          </Card>

          <FolderBrowser onWatch={watchFolder} watchedIds={watchedIds} />

          <Card>
            <SectionHeader title={`Inbox — files waiting for a deal (${inbox.length})`} />
            {!inbox.length
              ? <div style={{ fontSize: 13, color: B.textLt, padding: '8px 0' }}>
                  Nothing waiting. Files from unmapped folders show up here.
                </div>
              : inbox.map(i => (
                  <InboxRow key={i.inboxId} item={i} dealPickerItems={dealPickerItems}
                            onAssign={assign} onDismiss={dismiss} />
                ))}
          </Card>
        </>
      )}
    </div>
  );
};

export default ShareFileSettings;
